import { Layouts, RootRouteNames, RouteModes, RouteNames } from '@/types/enums';
import { RouteRecordRaw, createRouter, createWebHistory } from 'vue-router';
import PageCheck from '~icons/svg/PageCheck';
import Activity from '~icons/svg/activity';
import AddUser from '~icons/svg/add-user';
import BarChart01 from '~icons/svg/bar-chart-01';
import BarChartSquare from '~icons/svg/bar-chart-square-01';
import Bell from '~icons/svg/bell-02';
import Calendar from '~icons/svg/calendar';
import ChromeCast from '~icons/svg/chrome-cast';
import ClockIcon from '~icons/svg/clock';
import CreditCard from '~icons/svg/credit-card-01';
import Crown from '~icons/svg/crown01';
import Cube from '~icons/svg/cube';
import ActiveSessions from '~icons/svg/devices';
import Dotpoints from '~icons/svg/dotpoints-01';
import Events from '~icons/svg/events';
import Folder from '~icons/svg/folder';
import Headphones from '~icons/svg/headphones-01';
import Monitor from '~icons/svg/monitor';
import Password from '~icons/svg/password';
import PlusCircle from '~icons/svg/plus-circle';
import Receipt from '~icons/svg/receipt';
import Save from '~icons/svg/save';
import Security from '~icons/svg/security';
import Settings from '~icons/svg/settings';
import Signal from '~icons/svg/signal-02';
import SupportHeadphone from '~icons/svg/support-headphone';
import TicketIcon from '~icons/svg/ticket-01';
import Backup from '~icons/svg/upload-cloud.svg';
import UploadFile from '~icons/svg/upload-file';
import UserProfile from '~icons/svg/user-01';
import UsersIcon from '~icons/svg/users-01';
import VideoRecorder from '~icons/svg/video-recorder';

const Ticket = () => import('@/pages/Ticket/Ticket.vue');
const Channel = () => import('@/pages/Channel/Channel.vue');
const User = () => import('@/pages/User/User.vue');
const File = () => import('@/pages/File/File.vue');

export const navigableRoutes: RouteRecordRaw[] = [
  /*These routes are for the root of Rooms*/
  {
    path: '/#',
    name: RootRouteNames.RootRooms,
    component: {},
    meta: {
      root: RootRouteNames.Root,
      layout: Layouts.Panel,
      label: 'routes.root-rooms',
      icon: ChromeCast,
      aclName: ['stats', 'channels', 'users', 'files', 'recordings', 'sessions', 'connections'],
    },
  },
  {
    path: '/panel/stats',
    name: RouteNames.Stats,
    component: () => import('@/pages/Stats/Stats.vue'),
    meta: {
      root: RootRouteNames.RootRooms,
      layout: Layouts.Panel,
      label: 'routes.status',
      icon: BarChartSquare,
      guideLink: 'https://www.skyroom.online/textual-guide/status-header/',
      aclName: 'stats',
    },
  },
  {
    path: '/panel/channels',
    name: RouteNames.Channels,
    component: () => import('@/pages/Channels/Channels.vue'),
    meta: {
      root: RootRouteNames.RootRooms,
      layout: Layouts.Panel,
      label: 'routes.channels',
      icon: Dotpoints,
      guideLink: 'https://www.skyroom.online/textual-guide/channels/',
      aclName: 'channels',
    },
  },
  {
    path: '/panel/users',
    name: RouteNames.Users,
    component: () => import('@/pages/Users/Users.vue'),
    meta: {
      root: RootRouteNames.RootRooms,
      layout: Layouts.Panel,
      label: 'routes.users',
      icon: UsersIcon,
      guideLink: 'https://www.skyroom.online/textual-guide/users/',
      aclName: 'users',
    },
  },
  {
    path: '/panel/files',
    name: RouteNames.Files,
    component: () => import('@/pages/Files/Files.vue'),
    meta: {
      root: RootRouteNames.RootRooms,
      layout: Layouts.Panel,
      label: 'routes.files',
      icon: Folder,
      guideLink: 'https://www.skyroom.online/textual-guide/files/',
      aclName: 'files',
    },
  },
  {
    path: '/panel/recordings',
    name: RouteNames.Recordings,
    component: () => import('@/pages/Recordings/Recordings.vue'),
    meta: {
      root: RootRouteNames.RootRooms,
      layout: Layouts.Panel,
      label: 'routes.cloud-recording',
      icon: VideoRecorder,
      guideLink: 'https://www.skyroom.online/textual-guide/cloud-recording-in-management-panel/',
      aclName: 'recordings',
      hideInAfta: true,
    },
  },
  {
    path: '/panel/sessions',
    name: RouteNames.Sessions,
    component: () => import('@/pages/Sessions/Sessions.vue'),
    meta: {
      root: RootRouteNames.RootRooms,
      layout: Layouts.Panel,
      label: 'routes.sessions',
      icon: Calendar,
      guideLink: 'https://www.skyroom.online/textual-guide/sessions/',
      aclName: 'sessions',
    },
  },
  {
    path: '/panel/connections',
    name: RouteNames.Connections,
    component: () => import('@/pages/Connections/Connections.vue'),
    meta: {
      root: RootRouteNames.RootRooms,
      layout: Layouts.Panel,
      label: 'routes.connections',
      icon: Signal,
      guideLink: 'https://www.skyroom.online/textual-guide/connections/',
      aclName: 'connections',
    },
  },
  /* These routes are for the root of Events */
  {
    path: '/#',
    name: RootRouteNames.RootEvents,
    component: {},
    meta: {
      root: RootRouteNames.Root,
      layout: Layouts.Panel,
      label: 'routes.events',
      icon: Events,
      separator: 'bottom',
      hideInMenu: import.meta.env.VITE_IS_AFTA === 'true',
      isNew: true,
    },
  },
  {
    path: '/events/e/list',
    name: RouteNames.EventsDashboard,
    component: {},
    meta: {
      root: RootRouteNames.RootEvents,
      layout: Layouts.Panel,
      label: 'routes.events-list',
      icon: Dotpoints,
      externalLink: true,
      externalLinkTarget: '_self',
    },
  },
  {
    path: '/events/e/members',
    name: RouteNames.EventsMembers,
    component: {},
    meta: {
      root: RootRouteNames.RootEvents,
      layout: Layouts.Panel,
      label: 'routes.events-members',
      icon: UsersIcon,
      externalLink: true,
      externalLinkTarget: '_self',
    },
  },
  /* These routes are for the root of Services */
  {
    path: '/#',
    name: RootRouteNames.RootServices,
    component: {},
    meta: {
      root: RootRouteNames.Root,
      layout: Layouts.Panel,
      label: 'routes.root-services',
      icon: Crown,
      aclName: 'services',
    },
  },
  {
    path: '/panel/services',
    name: RouteNames.Services,
    component: () => import('@/pages/Services/Services.vue'),
    meta: {
      root: RootRouteNames.RootServices,
      layout: Layouts.Panel,
      label: 'routes.services',
      icon: Dotpoints,
      guideLink: 'https://www.skyroom.online/textual-guide/services/',
      aclName: 'services',
    },
  },
  {
    path: '/skyroom/order',
    name: RouteNames.NewOrder,
    component: {},
    meta: {
      root: RootRouteNames.RootServices,
      layout: Layouts.Panel,
      label: 'routes.order-new-service',
      icon: PlusCircle,
      externalLink: true,
      hideInAfta: true,
    },
  },
  /* These routes are for the root of Financial */
  {
    path: '/#',
    name: RootRouteNames.RootFinancial,
    component: {},
    meta: {
      root: RootRouteNames.Root,
      layout: Layouts.Panel,
      label: 'routes.root-financial',
      icon: CreditCard,
      aclName: 'invoices',
      hideInAfta: true,
    },
  },
  {
    path: '/panel/invoices',
    name: RouteNames.Invoices,
    component: () => import('@/pages/Invoices/Invoices.vue'),
    meta: {
      root: RootRouteNames.RootFinancial,
      layout: Layouts.Panel,
      label: 'routes.invoices',
      icon: Receipt,
      guideLink: 'https://www.skyroom.online/textual-guide/bill/',
      aclName: 'invoices',
      hideInAfta: true,
    },
  },
  /* These routes are for the root of Support */
  {
    path: '/#',
    name: RootRouteNames.RootSupport,
    component: {},
    meta: {
      root: RootRouteNames.Root,
      layout: Layouts.Panel,
      label: 'routes.root-support',
      icon: Headphones,
      separator: 'top',
      aclName: 'ticketing',
      hideInAfta: true,
    },
  },
  {
    path: '/panel/support',
    name: RouteNames.Support,
    component: () => import('@/pages/Support/Support.vue'),
    meta: {
      root: RootRouteNames.RootSupport,
      layout: Layouts.Panel,
      label: 'routes.support-list',
      icon: Dotpoints,
      guideLink: 'https://www.skyroom.online/textual-guide/ticket-support/',
      activeForChildren: false,
      aclName: 'ticketing',
    },
  },
  {
    path: '/panel/support/ticket/new',
    name: RouteNames.NewTicket,
    component: Ticket,
    meta: {
      root: RootRouteNames.RootSupport,
      parent: RouteNames.Support,
      layout: Layouts.Panel,
      label: 'support.add-new-ticket',
      mode: RouteModes.New,
      icon: TicketIcon,
      guideLink: 'https://www.skyroom.online/textual-guide/ticket-support/',
      aclName: 'ticketing',
    },
  },
  /* These routes are for the root of Settings */
  {
    path: '/#',
    name: RootRouteNames.RootSettings,
    component: {},
    meta: {
      root: RootRouteNames.Root,
      layout: Layouts.Panel,
      label: 'routes.root-settings',
      icon: Settings,
      isNeedSuperAdmin: true,
      aclName: 'settings',
    },
  },
  {
    path: '/panel/settings/file-upload',
    name: RouteNames.SettingFileUpload,
    component: () => import('@/pages/SettingFileUpload/SettingFileUpload.vue'),
    meta: {
      root: RootRouteNames.RootSettings,
      layout: Layouts.Panel,
      label: 'settings.upload-file',
      icon: UploadFile,
      aclName: 'settings',
    },
  },
  {
    path: '/panel/settings/access-management',
    name: RouteNames.SettingAccessManagement,
    component: () => import('@/pages/SettingAccessManagement/SettingAccessManagement.vue'),
    meta: {
      root: RootRouteNames.RootSettings,
      layout: Layouts.Panel,
      label: 'settings.access-management',
      icon: AddUser,
      aclName: 'settings',
    },
  },
  {
    path: '/panel/settings/backup-logs',
    name: RouteNames.SettingBackupLogs,
    component: () => import('@/pages/SettingBackupLogs/SettingBackupLogs.vue'),
    meta: {
      root: RootRouteNames.RootSettings,
      layout: Layouts.Panel,
      label: 'settings.backup-logs',
      icon: Backup,
      aclName: 'settings',
    },
  },
  {
    path: '/panel/settings/security',
    name: RouteNames.SettingSecurity,
    component: () => import('@/pages/SettingSecurity/SettingSecurity.vue'),
    meta: {
      root: RootRouteNames.RootSettings,
      layout: Layouts.Panel,
      label: 'settings.security',
      icon: Security,
      aclName: 'settings',
    },
  },
  {
    path: '/panel/settings/password-management',
    name: RouteNames.SettingPasswordManagement,
    component: () => import('@/pages/SettingPasswordManagement/SettingPasswordManagement.vue'),
    meta: {
      root: RootRouteNames.RootSettings,
      layout: Layouts.Panel,
      label: 'settings.password-management',
      icon: Password,
      aclName: 'settings',
    },
  },
  {
    path: '/panel/settings/active-sessions',
    name: RouteNames.SettingActiveSessions,
    component: () => import('@/pages/SettingActiveDevices/SettingActiveDevices.vue'),
    meta: {
      root: RootRouteNames.RootSettings,
      layout: Layouts.Panel,
      label: 'settings.active-sessions',
      icon: ActiveSessions,
      aclName: 'settings',
    },
  },
  /* These routes are for the root of Logs */
  {
    path: '/#',
    name: RootRouteNames.RootLogs,
    component: {},
    meta: {
      root: RootRouteNames.Root,
      layout: Layouts.Panel,
      label: 'routes.root-logs',
      icon: Activity,
      isNeedSuperAdmin: true,
      aclName: 'logs',
    },
  },
  {
    path: '/panel/logs/login-log-report',
    name: RouteNames.LogsLoginLogReport,
    component: () => import('@/pages/SettingLoginLogReport/SettingLoginLogReport.vue'),
    meta: {
      root: RootRouteNames.RootLogs,
      layout: Layouts.Panel,
      label: 'logs.login-log-report',
      icon: ClockIcon,
      aclName: 'logs',
    },
  },
  {
    path: '/panel/logs/activity-report',
    name: RouteNames.LogsActivity,
    component: () => import('@/pages/LogActivity/LogActivity.vue'),
    meta: {
      root: RootRouteNames.RootLogs,
      layout: Layouts.Panel,
      label: 'logs.activity',
      icon: BarChart01,
      aclName: 'logs',
    },
  },
  {
    path: '/panel/logs/product-version',
    name: RouteNames.LogsProductVersion,
    component: () => import('@/pages/LogProductVersion/LogProductVersion.vue'),
    meta: {
      root: RootRouteNames.RootLogs,
      layout: Layouts.Panel,
      label: 'logs.product-version',
      icon: Save,
      aclName: 'logs',
    },
  },
  {
    path: '/panel/logs/system-status',
    name: RouteNames.LogsSystemStatus,
    component: () => import('@/pages/SystemStatus/SystemStatus.vue'),
    meta: {
      root: RootRouteNames.RootLogs,
      layout: Layouts.Panel,
      label: 'logs.system-status',
      icon: Monitor,
      aclName: 'logs',
    },
  },
];

export const smallScreenNavigableRoutes: RouteRecordRaw[] = [
  {
    path: '/panel/notifications',
    name: RouteNames.Notifications,
    component: () => import('@/pages/Notifications/Notifications.vue'),
    meta: {
      layout: Layouts.Panel,
      label: 'routes.notifications',
      icon: Bell,
      separator: 'top',
      aclName: 'notifications',
    },
  },
  {
    path: '/panel/profile',
    name: RouteNames.Profile,
    component: () => import('@/pages/Profile/Profile.vue'),
    meta: {
      layout: Layouts.Panel,
      label: 'routes.profile',
      icon: UserProfile,
      guideLink: 'https://www.skyroom.online/textual-guide/general-familiarization-with-management-panel/',
      aclName: 'profile',
    },
  },
];

const routes: RouteRecordRaw[] = [
  ...navigableRoutes,
  ...smallScreenNavigableRoutes,
  {
    path: '/panel/channel/new',
    name: RouteNames.NewChannel,
    component: Channel,
    meta: {
      root: RootRouteNames.RootRooms,
      parent: RouteNames.Channels,
      layout: Layouts.Panel,
      label: 'routes.channels',
      mode: RouteModes.New,
    },
  },
  {
    path: '/panel/channel/:id/edit',
    name: RouteNames.EditChannel,
    component: Channel,
    meta: {
      root: RootRouteNames.RootRooms,
      parent: RouteNames.Channels,
      layout: Layouts.Panel,
      label: 'routes.channels',
      mode: RouteModes.Edit,
    },
  },
  {
    path: '/panel/user/new',
    name: RouteNames.NewUser,
    component: User,
    meta: {
      root: RootRouteNames.RootRooms,
      parent: RouteNames.Users,
      layout: Layouts.Panel,
      label: 'routes.users',
      mode: RouteModes.New,
    },
  },
  {
    path: '/panel/user/:id/edit',
    name: RouteNames.EditUser,
    component: User,
    meta: {
      root: RootRouteNames.RootRooms,
      parent: RouteNames.Users,
      layout: Layouts.Panel,
      label: 'routes.users',
      mode: RouteModes.Edit,
    },
  },
  {
    path: '/panel/file/:id/new',
    name: RouteNames.NewFile,
    component: File,
    meta: {
      root: RootRouteNames.RootRooms,
      parent: RouteNames.Files,
      layout: Layouts.Panel,
      label: 'routes.files',
      mode: RouteModes.New,
    },
  },
  {
    path: '/panel/file/:id/edit',
    name: RouteNames.EditFile,
    component: File,
    meta: {
      root: RootRouteNames.RootRooms,
      parent: RouteNames.Files,
      layout: Layouts.Panel,
      label: 'routes.files',
      mode: RouteModes.Edit,
    },
  },
  {
    path: '/panel/service/:id',
    name: RouteNames.Service,
    component: () => import('@/pages/Service/Service.vue'),
    meta: {
      root: RootRouteNames.RootServices,
      parent: RouteNames.Services,
      layout: Layouts.Panel,
      label: 'routes.services',
      icon: Cube,
    },
  },
  {
    path: '/panel/session/:id',
    name: RouteNames.Session,
    component: () => import('@/pages/Session/Session.vue'),
    meta: {
      root: RootRouteNames.RootRooms,
      parent: RouteNames.Sessions,
      layout: Layouts.Panel,
      label: 'routes.sessions',
      icon: PageCheck,
    },
  },
  {
    path: '/panel/recordings/:id',
    name: RouteNames.Recording,
    component: () => import('@/pages/Recording/Recording.vue'),
    meta: {
      root: RootRouteNames.RootRooms,
      parent: RouteNames.Recordings,
      layout: Layouts.Panel,
      label: 'routes.cloud-records',
      icon: PageCheck,
      hideInAfta: true,
    },
  },
  {
    path: '/panel/invoice/:id',
    name: RouteNames.Invoice,
    component: () => import('@/pages/Invoice/Invoice.vue'),
    meta: {
      root: RootRouteNames.RootFinancial,
      parent: RouteNames.Invoices,
      layout: Layouts.Panel,
      label: 'routes.invoice',
      icon: PageCheck,
    },
  },
  {
    path: '/panel/support/ticket/:id',
    name: RouteNames.EditTicket,
    component: Ticket,
    meta: {
      parent: RouteNames.Support,
      layout: Layouts.Panel,
      label: 'routes.support',
      mode: RouteModes.Edit,
      icon: SupportHeadphone,
      guideLink: 'https://www.skyroom.online/textual-guide/ticket-support/',
    },
  },
  {
    path: '/panel/sign-up',
    name: RouteNames.SignUp,
    component: () => import('@/pages/SignUp/SignUp.vue'),
    meta: {
      layout: Layouts.Auth,
      label: 'routes.sign-up',
    },
  },
  {
    path: '/panel/sign-in',
    name: RouteNames.SignIn,
    component: () => import('@/pages/SignIn/SignIn.vue'),
    meta: {
      layout: Layouts.Auth,
      label: 'routes.sign-in',
    },
  },
  {
    path: '/panel/forget-password',
    name: RouteNames.ForgetPassword,
    component: () => import('@/pages/ForgetPassword/ForgetPassword.vue'),
    meta: {
      layout: Layouts.Auth,
      label: 'routes.forget-password',
    },
  },
  {
    path: '/panel/verify',
    name: RouteNames.Verify,
    component: () => import('@/pages/VerifyUser/VerifyUser.vue'),
    meta: {
      layout: Layouts.Verify,
      label: 'routes.verify',
    },
  },
  {
    path: '/panel/',
    redirect: { name: RouteNames.SignIn },
  },
  {
    path: '/panel/:catchAll(.*)',
    redirect: { name: RouteNames.Stats },
  },
  {
    path: '/panel/settings/access-management/:id',
    name: RouteNames.SettingAccessManagementAccountDetail,
    component: () => import('@/pages/SettingAccessManagement/components/AccountDetails/AccountDetails.vue'),
    meta: {
      root: RootRouteNames.RootSettings,
      parent: RouteNames.SettingAccessManagement,
      layout: Layouts.Panel,
      label: 'settings.access-management',
      icon: AddUser,
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

let loadingTimer: ReturnType<typeof setTimeout> | null = null;
let isLoading = false;

const startRouteLoading = () => {
  loadingTimer = setTimeout(() => {
    isLoading = true;
    document.documentElement.classList.add('route-loading');
  }, 120);
};

const stopRouteLoading = () => {
  if (loadingTimer) {
    clearTimeout(loadingTimer);
    loadingTimer = null;
  }

  if (!isLoading) return;

  isLoading = false;
  document.documentElement.classList.remove('route-loading');
};

router.beforeEach(() => {
  startRouteLoading();
});

router.afterEach(() => {
  stopRouteLoading();
});

router.onError(() => {
  stopRouteLoading();
});

export default router;
